import posthog from "posthog-js";
import { computed, shallowRef, type ComputedRef } from "vue";
import type { User } from "@/types";
import type { EventName, EventPropertyKey } from "./events";

type PropertyValue = string | number | boolean | null | undefined;

type EventProperties<E extends EventName> = [EventPropertyKey<E>] extends [never]
  ? Record<string, never>
  : Partial<Record<EventPropertyKey<E>, PropertyValue>>;

const EU_INGESTION_HOST = "https://eu.i.posthog.com";

const TEXT_BEARING_ATTRIBUTES = new Set([
  "title",
  "alt",
  "placeholder",
  "aria-label",
  "aria-description",
  "aria-valuetext",
  "label",
  "value",
  "content",
]);

const projectKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const enabled = Boolean(projectKey) && import.meta.env.VITE_POSTHOG_DISABLED !== "true";

const loadedFeatureFlags = shallowRef<Record<string, string | boolean>>({});
let identifiedDistinctId: string | undefined;

export function initAnalytics() {
  if (!enabled) return;

  posthog.init(projectKey as string, {
    api_host: (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || EU_INGESTION_HOST,
    persistence: "localStorage",
    capture_pageview: false,
    disable_surveys: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
      maskAttributeFn: (name, value) => (TEXT_BEARING_ATTRIBUTES.has(name) ? "*".repeat(value.length) : value),
      blockSelector: 'img, picture, video, audio, input[type="hidden"], input[type="file"]',
      recordHeaders: false,
      recordBody: false,
    },
    enable_recording_console_log: false,
    autocapture: {
      css_selector_allowlist: ["a", "button", '[role^="menuitem"]', '[role="option"]', '[role="tab"]'],
    },
    mask_all_text: true,
    mask_all_element_attributes: false,
    advanced_disable_feature_flags_on_first_load: true,
  });

  posthog.onFeatureFlags((_flags, variants) => {
    if (posthog.get_distinct_id() === identifiedDistinctId) loadedFeatureFlags.value = variants;
  });
}

export function identifyUser(user: User) {
  if (!enabled) return;

  const distinctId = String(user.id);
  const alreadyIdentified = posthog.get_distinct_id() === distinctId;
  identifiedDistinctId = distinctId;

  posthog.identify(distinctId, {
    email: user.email,
    user_type: user.user_type,
    account_ids: user.account_ids,
  });
  if (alreadyIdentified) posthog.reloadFeatureFlags();
}

export function resetAnalytics() {
  if (!enabled) return;

  identifiedDistinctId = undefined;
  loadedFeatureFlags.value = {};
  posthog.reset();
}

export function setSurveyContext(surveyToken: string | undefined) {
  if (!enabled) return;

  if (surveyToken) posthog.register_for_session({ survey_token: surveyToken });
  else posthog.unregister_for_session("survey_token");
}

export function capturePageview(routeName: string | undefined) {
  if (!enabled) return;
  posthog.capture("$pageview", { route_name: routeName });
}

export function captureEvent<E extends EventName>(event: E, properties: EventProperties<E> = {}) {
  if (!enabled) return;
  posthog.capture(event, properties);
}

export function useFeatureFlag(key: string): ComputedRef<string | boolean | undefined> {
  return computed(() => (key in loadedFeatureFlags.value ? posthog.getFeatureFlag(key) : undefined));
}
